import type { ActionSummary } from '@bartizo/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { listActions } from '../api/actions.js';
import { useCurrentUser } from '../auth/session-context.js';
import { EmptyState } from '../components/EmptyState.js';
import { DataTable, type Column } from '../ui/DataTable.js';
import { PageHeader } from '../components/PageHeader.js';
import { ResultCounts } from '../components/ResultCounts.js';
import { describeAction, describeTarget } from '../lib/actions.js';
import { effectiveTimezone, formatInstant, formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';

/**
 * Actions (knowledge doc section 12): every ban and unban anyone asked for, newest
 * first, with where each one got to.
 *
 * Anyone signed in may read it — a viewer can see that an address was banned and
 * by whom, as section 17's "view" allows; asking for one is an operator's, from a
 * server's page or an event's. `?server_id=` narrows it to the actions that
 * reached one server, which is where the server page links.
 */

export function ActionsPage(): ReactNode {
  const navigate = useNavigate();
  const session = useCurrentUser();
  const timezone = effectiveTimezone(session.user.timezone, session.instanceTimezone);
  const [params] = useSearchParams();
  const serverId = params.get('server_id') ?? undefined;
  /** Cursors visited, for the server being filtered on; another filter starts over. */
  const [paging, setPaging] = useState<{ for?: string; history: (string | undefined)[] }>({
    history: [undefined],
  });
  const history = paging.for === serverId ? paging.history : [undefined];
  const setHistory = (next: (current: (string | undefined)[]) => (string | undefined)[]): void => {
    setPaging({ ...(serverId === undefined ? {} : { for: serverId }), history: next(history) });
  };
  const cursor = history[history.length - 1];

  const actions = useQuery({
    queryKey: ['actions', serverId ?? null, cursor ?? null],
    queryFn: () =>
      listActions({
        ...(serverId === undefined ? {} : { server_id: serverId }),
        ...(cursor === undefined ? {} : { cursor }),
      }),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="page">
      <PageHeader
        title="Actions"
        description={
          serverId === undefined ? (
            'Bans and unbans, and how each server answered.'
          ) : (
            <>
              The ones that reached{' '}
              <Link to={`/servers/${encodeURIComponent(serverId)}`}>{serverId}</Link>.{' '}
              <Link to="/actions">Show every action</Link>.
            </>
          )
        }
      />

      {actions.isError && (
        <Alert tone="danger" title="Could not load actions">
          {actions.error instanceof Error ? actions.error.message : 'Try again in a moment.'}
        </Alert>
      )}

      {actions.isPending && (
        <p className="row muted" aria-busy="true">
          <span className="spinner" aria-hidden="true" />
          Loading actions…
        </p>
      )}

      {actions.data?.items.length === 0 && (
        <EmptyState title="Nothing has been asked for yet">
          An operator bans an address from a server’s page or from an event that names one.
        </EmptyState>
      )}

      {actions.data !== undefined && actions.data.items.length > 0 && (
        <>
          <DataTable
            caption="Actions, newest first, with their results"
            columns={actionColumns(timezone)}
            rows={actions.data.items}
            rowKey={(action) => action.id}
            onSelect={(action) => {
              void navigate(`/actions/${encodeURIComponent(action.id)}`);
            }}
          />

          <div className="row">
            <Button
              disabled={history.length === 1}
              onClick={() => {
                setHistory((current) => current.slice(0, -1));
              }}
            >
              Previous
            </Button>
            <Button
              disabled={actions.data.nextCursor === null}
              onClick={() => {
                const next = actions.data.nextCursor;
                if (next !== null) setHistory((current) => [...current, next]);
              }}
            >
              Next
            </Button>
            <span className="muted text-sm">
              Page {history.length}
              {actions.isFetching ? ' · loading…' : ''}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

function actionColumns(timezone: string): Column<ActionSummary>[] {
  return [
    {
      key: 'action',
      header: 'Action',
      rowHeader: true,
      cell: (action) => (
        <>
          <Link to={`/actions/${encodeURIComponent(action.id)}`}>{describeAction(action)}</Link>
          {action.stoppedAt !== null && <span className="cell-sub">Stopped</span>}
        </>
      ),
    },
    { key: 'target', header: 'On', cell: (action) => describeTarget(action.target) },
    {
      key: 'by',
      header: 'Asked by',
      wideOnly: true,
      sortValue: (action) => action.requestedByEmail ?? action.source,
      cell: (action) => (
        <>
          {action.requestedByEmail ?? <span className="muted">{action.source}</span>}
          {action.source === 'policy' && <span className="cell-sub">automatic</span>}
        </>
      ),
    },
    {
      key: 'when',
      header: 'When',
      sortValue: (action) => Date.parse(action.createdAt),
      cell: (action) => (
        <span title={formatInstant(action.createdAt, { timezone })}>
          {formatRelative(action.createdAt)}
        </span>
      ),
    },
    {
      key: 'results',
      header: 'Results',
      cell: (action) => <ResultCounts counts={action.counts} />,
    },
  ];
}
